import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Info, Save, Search,
  BookOpen, FileText, BarChart3, Receipt, FileCheck2, HandCoins, Factory, Upload, Download, DatabaseBackup, Users } from "lucide-react";

const ICONS = { dasar: BookOpen, invoice: FileText, laporan: BarChart3, pajak_usaha: Receipt, pajak_pribadi: FileCheck2,
  hutang_piutang: HandCoins, produksi: Factory, impor_mutasi: Upload, ekspor: Download, backup: DatabaseBackup, multi_user: Users };
const EMPTY = { name: "", description: "", target: "bisnis", active: true, is_trial: false, trial_days: 14, is_free: false, is_default: false,
  price_monthly: 0, price_yearly: 0, max_users: -1, max_pabrik: -1, features: ["dasar"], sort: 0 };
const TRIAL_PRESETS = [7, 14, 30];

const Toggle = ({ label, hint, checked, onChange, testid }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <div><div className="text-sm text-slate-800">{label}</div>{hint && <div className="text-[11px] text-slate-400">{hint}</div>}</div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid}/>
  </div>
);

const Num = ({ label, value, onChange, testid, note }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Input type="number" value={value} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testid}/>
    {note && <p className="text-[11px] text-slate-400">{note}</p>}
  </div>
);

function FeatureGrid({ features, value, target, onChange }) {
  const [q, setQ] = useState("");
  const list = features.filter((f) => (f.t === "semua" || f.t === target) && f.l.toLowerCase().includes(q.toLowerCase()));
  const all = list.every((f) => value.includes(f.k));
  const toggle = (k) => k !== "dasar" && onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  const toggleAll = () => onChange(all ? ["dasar"] : Array.from(new Set([...value, ...list.map((f) => f.k)])));
  return (
    <div className="card-flat p-5 md:p-6" data-testid="plan-features">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-slate-900">Fitur</h3>
        <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "var(--brand-soft)", color: "var(--brand)" }} data-testid="plan-features-count">{value.length} Dipilih</span>
      </div>
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input className="pl-8" placeholder="Cari fitur..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="plan-features-search"/></div>
        <Button type="button" variant="outline" onClick={toggleAll} data-testid="plan-features-all">{all ? "Hapus Semua" : "Pilih Semua"}</Button>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {list.map((f) => {
          const Icon = ICONS[f.k] || BookOpen; const on = value.includes(f.k);
          return (
            <label key={f.k} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors duration-150 ${on ? "border-[color:var(--brand)] bg-[color:var(--brand-soft)]" : "border-slate-200 hover:border-slate-300"}`} data-testid={`plan-feature-${f.k}`}>
              <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 text-white" style={{ background: "var(--brand)" }}><Icon size={16}/></span>
              <span className="flex-1 min-w-0"><span className="block text-sm font-medium text-slate-800">{f.l}{f.wajib && <span className="text-[10px] text-slate-400"> (wajib)</span>}</span>
                <span className="block text-[11px] text-slate-500 leading-snug mt-0.5">{f.d}</span></span>
              <Checkbox checked={on} disabled={f.k === "dasar"} onCheckedChange={() => toggle(f.k)}/>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export function PlanForm({ plan, features, onBack, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...(plan || {}) });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Nama paket wajib diisi");
    setSaving(true);
    const body = { ...f, trial_days: Number(f.trial_days || 0), price_monthly: Number(f.price_monthly || 0), price_yearly: Number(f.price_yearly || 0),
      max_users: Number(f.max_users === "" ? -1 : f.max_users), max_pabrik: Number(f.max_pabrik === "" ? -1 : f.max_pabrik), sort: Number(f.sort || 0) };
    try { plan?.id ? await api.put(`/sa/paket/${plan.id}`, body) : await api.post("/sa/paket", body); toast.success("Paket disimpan"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-5" data-testid="plan-form">
      <div className="flex items-center justify-between gap-3">
        <div><h2 className="text-xl font-semibold text-slate-900">{plan?.id ? "Ubah Paket" : "Buat Paket"}</h2>
          <p className="text-sm text-slate-500">Atur harga, batas, dan fitur yang didapat pelanggan.</p></div>
        <Button variant="outline" onClick={onBack} data-testid="plan-back"><ArrowLeft size={14} className="mr-1.5"/> Kembali</Button>
      </div>
      <div className="grid lg:grid-cols-[300px_1fr] gap-5">
        <div className="space-y-5">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 flex gap-2"><Info size={16} className="shrink-0 mt-0.5"/>
            <span>Fitur "Pencatatan Dasar" selalu termasuk. Menu di luar fitur paket otomatis terkunci untuk pelanggan.</span></div>
          <div className="card-flat p-5">
            <div className="font-semibold text-slate-900 mb-1">Pengaturan Cepat</div>
            <Toggle label="Aktif" hint="Tampil & bisa dibeli" checked={f.active} onChange={set("active")} testid="plan-active"/>
            <Toggle label="Masa Coba" hint="Pendaftar baru mencoba gratis" checked={f.is_trial} onChange={set("is_trial")} testid="plan-trial"/>
            {f.is_trial && (
              <div className="pb-2 space-y-2" data-testid="plan-trial-days-wrap">
                <div className="flex gap-1.5">{TRIAL_PRESETS.map((d) => (
                  <button key={d} type="button" onClick={() => set("trial_days")(d)} data-testid={`plan-trial-${d}`}
                    className={`flex-1 text-xs py-1.5 rounded-md border ${Number(f.trial_days) === d ? "border-[color:var(--brand)] brand-text bg-[color:var(--brand-soft)]" : "border-slate-200 text-slate-600"}`}>{d} hari</button>))}</div>
                <Input type="number" value={f.trial_days} onChange={(e) => set("trial_days")(e.target.value)} placeholder="Jumlah hari" data-testid="plan-trial-days"/>
              </div>
            )}
            <Toggle label="Gratis" hint="Tanpa batas waktu, tanpa bayar" checked={f.is_free} onChange={set("is_free")} testid="plan-free"/>
            <Toggle label="Paket Default" hint="Otomatis untuk pendaftar baru" checked={f.is_default} onChange={set("is_default")} testid="plan-default"/>
          </div>
          <div className="card-flat p-5 space-y-4">
            <div className="font-semibold text-slate-900">Harga (Rp)</div>
            <Num label="Bulanan" value={f.price_monthly} onChange={set("price_monthly")} testid="plan-price-monthly"/>
            <Num label="Tahunan" value={f.price_yearly} onChange={set("price_yearly")} testid="plan-price-yearly"/>
          </div>
        </div>
        <div className="space-y-5 min-w-0">
          <div className="card-flat p-5 md:p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">Informasi Paket</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1.5 md:col-span-1"><Label>Nama Paket *</Label><Input value={f.name} onChange={set("name")} placeholder="Mis. Bisnis Pro" data-testid="plan-name"/></div>
              <div className="space-y-1.5"><Label>Untuk</Label>
                <div className="grid grid-cols-2 gap-1.5">{[["bisnis", "Bisnis"], ["pribadi", "Pribadi"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => set("target")(k)} data-testid={`plan-target-${k}`}
                    className={`text-sm py-2 rounded-md border-2 ${f.target === k ? "border-[color:var(--brand)] brand-text bg-[color:var(--brand-soft)]" : "border-slate-200 text-slate-600"}`}>{l}</button>))}</div></div>
              <Num label="Urutan Tampil" value={f.sort} onChange={set("sort")} testid="plan-sort"/>
              <Num label="Maks. User" value={f.max_users} onChange={set("max_users")} testid="plan-max-users" note='"-1" = tanpa batas'/>
              {f.target === "bisnis" && <Num label="Maks. Pabrik" value={f.max_pabrik} onChange={set("max_pabrik")} testid="plan-max-pabrik" note='"-1" = tanpa batas'/>}
            </div>
            <div className="space-y-1.5"><Label>Deskripsi</Label><Textarea rows={3} value={f.description} onChange={set("description")} placeholder="Ringkasan paket untuk pelanggan" data-testid="plan-description"/></div>
          </div>
          <FeatureGrid features={features} value={f.features} target={f.target} onChange={set("features")}/>
          <div className="flex justify-end"><Button onClick={save} disabled={saving} className="brand-btn rounded-sm" data-testid="plan-save"><Save size={14} className="mr-1.5"/>{saving ? "Menyimpan..." : "Simpan Paket"}</Button></div>
        </div>
      </div>
    </div>
  );
}
